import { PINYIN_SYLLABLE_KEYS } from "#pinyin-syllable-keys.gen";

/**
 * Web resolves pinyin syllable audio as PLAIN URLs under `public/pinyin-audio/`, and deliberately
 * does NOT use the `require.context` the native variant relies on.
 *
 * `require.context` is a BUILD-TIME directive: it registers every matching file as its own Metro
 * module, so the ~2k syllable mp3s became ~6.2k module references — 56% of the entire web module
 * graph (7,994 modules / 34.7MB). That cost is paid on every dev SSR document request, because
 * Expo's dev server runs a full static serialization of the client graph just to compute the
 * `<script src>` tags (`getStaticResourcesAsync` → `serializerOutput: "static"`). Measured: ~7.5s
 * per document with the mp3 context, ~3.0s without it, for a bundle only 1.4MB smaller — i.e. the
 * cost is module COUNT, not bytes. Runtime laziness cannot help; the graph is built at bundle time.
 *
 * Serving them from `public/` instead means zero Metro modules, and the browser caches each file
 * individually on first play rather than paying for all of them up front.
 *
 * The key list is generated (see `pinyin-syllable-keys.gen.ts`) from the same source directory the
 * files are synced from, so existence checks stay exact without touching the filesystem at runtime.
 */
const PINYIN_AUDIO_PUBLIC_BASE_PATH = "/pinyin-audio";

const pinyinMp3AssetKeys: ReadonlySet<string> = new Set(PINYIN_SYLLABLE_KEYS);

/** Whether a pinyin mp3 exists for the given key. */
export function hasPinyinMp3Asset(key: string): boolean {
  return pinyinMp3AssetKeys.has(key);
}

/**
 * Resolve the public URL for a pinyin syllable's mp3, or `null` when the key has no audio.
 * `expo-audio`'s `AudioSource` accepts a URL string, matching the native variant's contract.
 */
export function getPinyinMp3Asset(key: string): number | string | null {
  if (!pinyinMp3AssetKeys.has(key)) {
    return null;
  }
  return `${PINYIN_AUDIO_PUBLIC_BASE_PATH}/${key}.mp3`;
}
